<template>
  <div
    v-if="!isOnline"
    class="flex sticky top-0 z-50 w-full bg-gray-200 place-content-center"
  >
    <p class="m-2">You are currently in offline mode</p>
  </div>
  <header>
    <!-- HEADER DESKTOP -->
    <div
      class="header_graphic block text-white px-8 md:px-12 py-6 sm:py-12 2xl:px-64 bg-no-repeat"
    >
      <div class="container mx-auto">
        <div class="flex">
          <div class="flex-1 grow">
            <router-link :to="isPropertyLoading ? '' : `/${bldgStore?.siteCode}`">
              <img
                :src="safetyNetLogoPath"
                alt="Site Logo"
                class="sm:max-h-16 max-h-10"
              />
              <span class="md:hidden block mt-1" v-if="property?.name">
                <div v-if="customLogoPath">
                  <img
                    :src="customLogoPath"
                    class="h-16"
                    :class="enableBuildingPicker ? 'cursor-pointer' : ''"
                    @click="enableBuildingPicker ? (buildingPickerVisible = true) : {}"
                  />
                </div>
                <div v-else>
                  Life Safety at
                  <span
                    :class="enableBuildingPicker ? 'cursor-pointer' : ''"
                    @click="enableBuildingPicker ? (buildingPickerVisible = true) : {}"
                  >
                    {{ property?.name }}
                  </span>
                </div>
              </span>
            </router-link>
          </div>
          <div v-if="property && !isPropertyLoading">
            <div v-if="customLogoPath">
              <div class="text-newlg md:w-60 mb-4">
                <span class="hidden md:block" v-if="isLoggedIn">
                  Welcome {{ user?.firstName ?? "" }} to
                </span>
                <img
                  :src="customLogoPath"
                  alt="Site Logo"
                  class="max-h-24 hidden md:block"
                />
              </div>
              <div class="text-nav md:flex">
                <div class="flex-1 md:block hidden">
                  <router-link :to="isPropertyLoading ? '' : `/${bldgStore?.siteCode}`">
                    Home
                  </router-link>
                </div>
                <div class="flex-1 md:flex-row flex-col" v-if="isOnline">
                  <router-link
                    v-if="isLoggedIn"
                    :to="'/' + property?.siteCode + '/account'"
                  >
                    Account
                  </router-link>
                  <router-link
                    v-else
                    data-cy="LoginPageLink"
                    :to="'/' + property?.siteCode + '/login'"
                  >
                    Login
                  </router-link>
                </div>
                <div class="flex-1 pl-4" v-if="isAdmin && isOnline">
                  <router-link :to="'/' + property?.siteCode + '/admin'">
                    Admin
                  </router-link>
                </div>
              </div>
            </div>
            <div v-else>
              <div class="text-newlg md:w-60 mb-4">
                <span class="hidden md:block" v-if="isLoggedIn">
                  Welcome {{ user?.firstName ?? "" }} to
                </span>
                <span class="hidden md:block">
                  Life Safety at
                  <span
                    :class="enableBuildingPicker ? 'cursor-pointer' : ''"
                    @click="enableBuildingPicker ? (buildingPickerVisible = true) : {}"
                  >
                    {{ property?.name }}
                  </span>
                </span>
              </div>
              <div class="text-nav md:flex">
                <div class="flex-1 md:block hidden">
                  <router-link :to="isPropertyLoading ? '' : `/${bldgStore?.siteCode}`">
                    Home
                  </router-link>
                </div>
                <div class="flex-1 md:flex-row flex-col" v-if="isOnline">
                  <router-link
                    v-if="isLoggedIn"
                    :to="'/' + property?.siteCode + '/account'"
                  >
                    Account
                  </router-link>
                  <router-link
                    v-else
                    data-cy="LoginPageLink"
                    :to="'/' + property?.siteCode + '/login'"
                  >
                    Login
                  </router-link>
                </div>
                <div class="flex-1 pl-4" v-if="isAdmin && isOnline">
                  <router-link :to="'/' + property?.siteCode + '/admin'">
                    Admin
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="bg-lightblue mt-0.5 h-6 xl:h-9 xl:mt-1"></div>
  </header>
  <BasePopup
    v-if="enableBuildingPicker"
    :isVisible="buildingPickerVisible"
    title="Choose a Building"
    :is-full-width="false"
    @close="buildingPickerVisible = false"
  >
    <button
      v-for="r in user?.allRegistrations"
      class="w-full px-4 pr-8 py-3 mt-2 text-blue-900 bg-blue-100 border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500 text-left flex"
    >
      <i-heroicons-outline-check
        class="w-6 h-6 mr-2 m-auto"
        :class="property?.siteCode === r.siteCode ? '' : 'invisible'"
      />
      <a :href="`/${r.siteCode}`" class="flex-grow">
        <div class="text-lg">{{ r.clientName }}: {{ r.propertyName }}</div>
      </a>
    </button>
  </BasePopup>
</template>

<script setup lang="ts">
import { watch, ref, computed, onMounted } from "vue";
import { useBldgStore } from "../store/bldgStore";
import { useUserStore } from "../store/userStore";
import { useUtilityStore } from "../store/utilityStore";

const contentApiPath = "/api/property/content/file";
const safetyNetLogoPath = "/images/safetyNetLogo.png";
var customLogoPath = ref("");
var bldgStore = useBldgStore();
var userStore = useUserStore();
const utilityStore = useUtilityStore();
var isPropertyLoading = computed(() => bldgStore.isLoading);
var property = computed(() => bldgStore.property);
var isLoggedIn = computed(() => userStore.isLoggedIn);
var user = computed(() => userStore.user);
var isAdmin = computed(() => userStore.isAdmin);
let isLoading = ref(false);

utilityStore.setupOfflineListeners();
var isOnline = computed(() => utilityStore.isOnline);

var enableBuildingPicker = computed(() => {
  return !isLoading.value && (user.value?.allRegistrations?.length ?? 0) > 1;
});

var buildingPickerVisible = ref(false);

const props = defineProps<{
  title?: string;
}>();

// Sets document.title. A <title> element used to sit in this component's template,
// but a <title> outside <head> is inert, so tabs showed the raw URL instead.
useTitle(computed(() => (props.title ? `${props.title} - SafetyNet` : "SafetyNet")));

onMounted(async () => await loadData());
watch(property, loadData);
async function loadData() {
  isLoading.value = true;
  let forceLogin = location.search.indexOf("login") >= 0;

  if (property?.value?.portalId) {
    let customLogo = property.value.content?.find((c) => c.contentType === 1);
    if (customLogo)
      customLogoPath.value = `${contentApiPath}/${property.value.siteCode}/${customLogo.contentId}`;

    try {
      await userStore.getUser(
        property.value.siteCode!,
        property.value.portalId!,
        forceLogin,
        forceLogin ? property.value.clientName! : ""
      );

      console.log("Training header> User load complete", user.value);
    } catch (e: any) {
      console.warn("Training Header Error loading user", e);
    }
  }
  isLoading.value = false;
}
</script>

<style>
.header_graphic {
  background-image: url("/images/bg_hdr_tall_wide.png"),
    linear-gradient(
      90.81deg,
      rgba(5, 58, 108, 0.8) 0.95%,
      rgba(0, 49, 97, 0.8) 18.75%,
      rgba(1, 31, 60, 0.8) 53.5%
    );
  background-color: #03366a;
}
</style>
